import React, { useEffect, useState, Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { CouponModal } from '../components/modals/CouponModal';
import { CongratulationsModal } from '../components/modals/CongratulationsModal';
import { ScratchAndWinModal } from '../components/modals/ScratchAndWinModal';
import { SectionSkeleton } from '../components/shared/SkeletonLoader';
import { TicketIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { OffersHeroBanner } from '../components/offers/OffersHeroBanner';
import { OffersEngagementCards } from '../components/offers/OffersEngagementCards';
import { OffersFlashSales } from '../components/offers/OffersFlashSales';
import { OffersDealOfDay } from '../components/offers/OffersDealOfDay';
import { LimitedTimeDeals } from '../components/offers/LimitedTimeDeals';
import { EndingSoonDeals } from '../components/offers/EndingSoonDeals';
import { OffersHealthWellness } from '../components/offers/OffersHealthWellness';
import { BrandDeals } from '../components/offers/BrandDeals';
import { TopOffers } from '../components/home/TopOffers';
import { ShopByCategory } from '../components/home/ShopByCategory';
import { OffersBannerRow } from '../components/offers/OffersBannerRow';
import { OffersFullWidthBanner } from '../components/offers/OffersFullWidthBanner';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { PullToRefresh } from '../components/shared/PullToRefresh';
const TrendingProducts = lazy(() => import('../components/home/TrendingProducts').then(m => ({
  default: m.TrendingProducts
})));
const ExclusiveDeals = lazy(() => import('../components/home/ExclusiveDeals').then(m => ({
  default: m.ExclusiveDeals
})));
const EnhancedClearanceSection = lazy(() => import('../components/home/EnhancedClearanceSection').then(m => ({
  default: m.EnhancedClearanceSection
})));
const tabs: {
  id: string;
  label: string;
  sectionId?: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'flash',
  label: '⚡ Flash',
  sectionId: 'flash-sales'
}, {
  id: 'deal-of-day',
  label: '🔥 Deal of Day',
  sectionId: 'deal-of-day'
}, {
  id: 'trending',
  label: '📈 Trending',
  sectionId: 'trending-now'
}, {
  id: 'exclusive',
  label: '💎 Exclusive',
  sectionId: 'exclusive-deals'
}, {
  id: 'clearance',
  label: '🏷️ Clearance',
  sectionId: 'clearance-sale'
}, {
  id: 'limited',
  label: 'Limited time',
  sectionId: 'limited-time-deals'
}, {
  id: 'ending-soon',
  label: 'Ending soon',
  sectionId: 'ending-soon-deals'
}, {
  id: 'brands',
  label: 'Brand deals',
  sectionId: 'brand-deals'
}];
export function OffersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNowContext = searchParams.get('from') === 'now';
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [spinModalOpen, setSpinModalOpen] = useState(false);
  const [scratchModalOpen, setScratchModalOpen] = useState(false);
  const handleTabClick = (tab: (typeof tabs)[number]) => {
    setActiveTab(tab.id);
    if (tab.sectionId) {
      const el = document.getElementById(tab.sectionId);
      if (el) {
        const offset = window.innerWidth >= 768 ? 240 : 170;
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
        // brief highlight
        el.classList.add('ring-2', 'ring-[#fdc300]', 'ring-offset-2', 'rounded-xl');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-[#fdc300]', 'ring-offset-2', 'rounded-xl');
        }, 1500);
      }
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  // Update active tab based on which section is most visible
  useEffect(() => {
    const handler = () => {
      const sectionTabs = tabs.filter(t => t.sectionId);
      let current = 'all';
      const viewportMid = window.innerHeight / 2;
      for (const t of sectionTabs) {
        const el = document.getElementById(t.sectionId!);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportMid && rect.bottom >= viewportMid) {
          current = t.id;
          break;
        }
      }
      setActiveTab(current);
    };
    window.addEventListener('scroll', handler, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return <HelmetProvider data-id="element-7368" data-mp="1nm7xufkpb3xu">
      <SEO title="Deals & Offers - Save Up to 70% on Health Products" description="Discover amazing deals and exclusive offers at Innova Pharmacy. Save up to 70% on health, beauty, and wellness products with fast delivery across Saudi Arabia." canonical="/en/offers/" robots="index, follow, max-image-preview:large" hreflangAr="/offers/" hreflangEn="/en/offers/" hreflangDefault="/offers/" ogImage="https://cdn.innovaksa.net/og/offers.jpg" keywords="pharmacy deals Saudi Arabia, health product offers, discount supplements, beauty sale, vitamins on sale, pharmacy promotions Riyadh, cheap medications, wellness deals" data-id="element-7369" data-mp="1nlq2dl5v4tqz" />
      <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden pb-32 md:pb-0" data-id="element-7370" data-mp="6g8ai3mqh0ai">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7371" data-mp="g7cop14l4h4a">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-7372" data-mp="g6b1xze7i66q" />
        </div>
        {/* Mobile Header Overlay */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7373" data-mp="2vyqsj039bmd6">
          <MobileInternalHeader title="Deals & Offers" data-id="element-7374" data-mp="2r0fiopcypbuy" />
        </div>

        <PullToRefresh onRefresh={async () => {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }} data-id="element-7375" data-mp="16hmvvf2h65bb">
          <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-7376" data-mp="1bfws7szxrbyi">
            <h1 className="sr-only" data-id="element-7377" data-mp="wqs6w2xugrm6">
              Deals & Offers - Save Big on Health and Beauty Products
            </h1>
            <PageHeroBreadcrumb breadcrumbs={[{
            label: 'Home',
            href: '/'
          }, {
            label: 'Offers'
          }]} title="Deals & Offers" description="Discover amazing deals and exclusive offers at Innova Pharmacy. Save up to 70% on health, beauty, and wellness products with fast delivery across Saudi Arabia." data-id="element-7378" data-mp="11otrwtetz10a" />

            {/* Sticky Mobile Tab Bar — scrolls to section + tracks active */}
            <div className="md:hidden sticky top-[56px] z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm" data-id="element-7379" data-mp="2d4qrrco7j5ca">
              <div className="overflow-x-auto scrollbar-hide px-4 py-2.5" data-id="element-7380" data-mp="10vn81gm2fdyd">
                <div className="flex gap-2" data-id="element-7381" data-mp="vyeqni8qrdy6">
                  {tabs.map(tab => <button key={tab.id} onClick={() => handleTabClick(tab)} className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-[#1E2B55] text-white shadow-sm' : 'bg-gray-100 text-gray-700 active:bg-gray-200'}`} data-id="element-7382" data-mp="r5v9kbaui0om">
                      {tab.label}
                    </button>)}
                </div>
              </div>
            </div>

            <section className="py-2 md:py-6" data-id="element-7383" data-mp="39zmxrv28i25">
              <OffersHeroBanner data-id="element-7384" data-mp="3b0v9okoyqg6" />
            </section>
            <section className="container mx-auto pb-3 md:pb-6" data-id="element-7385" data-mp="22kh9x6xt7jfb">
              <OffersEngagementCards onOpenCoupons={() => setCouponModalOpen(true)} onOpenSpin={() => setSpinModalOpen(true)} onOpenScratch={() => setScratchModalOpen(true)} data-id="element-7386" data-mp="1xm7csqsiy8r2" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7387" data-mp="2a38dbbtuppj9">
              <OffersBannerRow columns={6} bannerSpec={{
              width: 220,
              height: 220,
              ratio: '1:1'
            }} count={6} data-id="element-7388" data-mp="2evrpc4cw4cly" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7389" data-mp="1m9qj0thd350n">
              <TopOffers data-id="element-7390" data-mp="1r7i71cevy6zk" />
            </section>
            <section id="limited-time-deals" className="py-1 md:py-4 scroll-mt-32" data-id="element-7391" data-mp="1r8a46ro4scry">
              <LimitedTimeDeals data-id="element-7392" data-mp="2156448zyrqkw" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7393" data-mp="215y2frky42ev">
              <OffersBannerRow columns={3} bannerSpec={{
              width: 440,
              height: 248,
              ratio: '16:9'
            }} count={3} data-id="element-7394" data-mp="37l8fevd1fhf0" />
            </section>
            <section id="flash-sales" className="py-1 md:py-4 scroll-mt-32" data-id="element-7395" data-mp="1mxf88tg86f6v">
              <OffersFlashSales data-id="element-7396" data-mp="1vrtmqtsxllox" />
            </section>
            <section id="ending-soon-deals" className="py-1 md:py-4 scroll-mt-32" data-id="element-7397" data-mp="1vs3aeaqi7grg">
              <EndingSoonDeals data-id="element-7398" data-mp="20sh9h3a4ngdc" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7399" data-mp="141fb748e4lgf">
              <OffersBannerRow columns={3} bannerSpec={{
              width: 360,
              height: 270,
              ratio: '4:3'
            }} count={6} data-id="element-7400" data-mp="142qe71u0hp6h" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7401" data-mp="1i1q6cjgqgm7e">
              <ShopByCategory data-id="element-7402" data-mp="2oghbx0gswv7r" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7403" data-mp="2ofpdlhvtkjds">
              <OffersHealthWellness data-id="element-7404" data-mp="2tf23kbhzm6kl" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7405" data-mp="2tdh8mkrrglm7">
              <OffersBannerRow columns={4} bannerSpec={{
              width: 320,
              height: 240,
              ratio: '4:3'
            }} count={4} data-id="element-7406" data-mp="hsi0i9rpdo3n" />
            </section>
            <section id="deal-of-day" className="py-1 md:py-4 scroll-mt-32" data-id="element-7407" data-mp="802mtonfvlok">
              <OffersDealOfDay data-id="element-7408" data-mp="3v6v2yp0mobd2" />
            </section>
            <section id="brand-deals" className="py-1 md:py-4 scroll-mt-32" data-id="element-7409" data-mp="3v8fzulaf06e3">
              <BrandDeals data-id="element-7410" data-mp="3q9v1kreq3wsa" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7411" data-mp="3l7vwnd3c4rt8">
              <OffersBannerRow columns={2} bannerSpec={{
              width: 680,
              height: 382,
              ratio: '16:9'
            }} count={2} data-id="element-7412" data-mp="3lartsz7czcgm" />
            </section>
            <section id="trending-now" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7413" data-mp="1ejahd9mgnk6h">
              <Suspense fallback={<SectionSkeleton data-id="element-7415" data-mp="3dp65l1oe5jiq" />} data-id="element-7414" data-mp="38wd1mg0qyf7p">
                <TrendingProducts data-id="element-7416" data-mp="3dq7rk16y3qf8" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7417" data-mp="2z0s34ruvzx2p">
              <OffersFullWidthBanner data-id="element-7418" data-mp="2z2364pgid0sr" />
            </section>
            <section id="exclusive-deals" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7419" data-mp="33ylavpbu8z53">
              <Suspense fallback={<SectionSkeleton data-id="element-7421" data-mp="3rn4is3qz79wn" />} data-id="element-7420" data-mp="3rpqmfsaqx59l">
                <ExclusiveDeals data-id="element-7422" data-mp="iw9ox6oicco" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7423" data-mp="3ioj1sd15k2xz">
              <OffersBannerRow columns={6} bannerSpec={{
              width: 260,
              height: 346,
              ratio: '3:4'
            }} count={6} data-id="element-7424" data-mp="3inpsb4at6zc5" />
            </section>
            <section id="clearance-sale" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7425" data-mp="2bb7a1g50rjfp">
              <Suspense fallback={<SectionSkeleton data-id="element-7427" data-mp="23t9c88w79wsb" />} data-id="element-7426" data-mp="23plk9e6elifp">
                <EnhancedClearanceSection data-id="element-7428" data-mp="1yslbgvi661kw" />
              </Suspense>
            </section>
          </main>
        </PullToRefresh>
        <Footer data-id="element-7429" data-mp="2do1bdh5fkrp7" />
        <MobileFooterNav data-id="element-7430" data-mp="2doku302wnk4a" />
        <PharmacistChat data-id="element-7431" data-mp="2dmzw10hdtj1o" />
        <div className="h-20 md:hidden" data-id="element-7432" data-mp="28pq33ausap93"></div>

        {/* Floating Apply Coupon CTA (Mobile Only) */}
        <button onClick={() => setCouponModalOpen(true)} className="md:hidden fixed bottom-[80px] left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-gradient-to-r from-[#fdc300] to-[#ffd84d] text-[#1E2B55] rounded-full shadow-xl px-5 py-3 font-bold text-sm active:scale-95 transition-transform" aria-label="Apply a coupon" data-id="element-7433" data-mp="1l05di944ktqu">
          <TicketIcon className="w-4 h-4" data-id="element-7434" data-mp="12a79q57d1mt0" />
          Apply Coupon
        </button>

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7435" data-mp="1282vatbu55eh" />
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => {
        toast.success(`Coupon ${code} copied! Apply at checkout.`);
        setCouponModalOpen(false);
      }} data-id="element-7436" data-mp="4m415vpkzrv2" />
        <CongratulationsModal isOpen={spinModalOpen} onClose={() => setSpinModalOpen(false)} data-id="element-7437" data-mp="jaqi21uuwroe" />
        <ScratchAndWinModal isOpen={scratchModalOpen} onClose={() => setScratchModalOpen(false)} data-id="element-7438" data-mp="o7yy9wwg2ln0" />
      </div>
    </HelmetProvider>;
}